import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Ticket, Send, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS = ["aberto", "andamento", "resolvido"];
const LABEL = { aberto: "Aberto", andamento: "Em andamento", resolvido: "Resolvido" };
const PILL = { aberto: "border-amber-700 text-amber-200 bg-amber-950/30", andamento: "border-sky-700 text-sky-200 bg-sky-950/30", resolvido: "border-green-700 text-green-200 bg-green-950/30" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "");

function Thread({ t, onChanged, focus }) {
  const [open, setOpen] = useState(!!focus);
  const boxRef = useRef(null);
  useEffect(() => { if (focus) { boxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } }, [focus]);
  const [img, setImg] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && t.has_screenshot && !img) { try { const { data } = await api.get(`/admin/support/tickets/${t.id}/screenshot`); setImg(data.data_url); } catch {} }
  };
  const change = async (status) => { try { await api.patch(`/admin/support/tickets/${t.id}/status`, { status }); onChanged({ ...t, status }); } catch (e) { toast.error(formatApiError(e)); } };
  const send = async () => {
    const text = reply.trim(); if (!text) return;
    setSending(true);
    try { const { data } = await api.post(`/admin/support/tickets/${t.id}/reply`, { text }); setReply(""); onChanged(data); toast.success("Resposta enviada"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };
  return (
    <div ref={boxRef} data-testid={`admin-ticket-${t.id}`} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 space-y-2">
      <button type="button" onClick={toggle} className="w-full flex items-start justify-between gap-2 text-left">
        <div className="min-w-0">
          <div className="font-bold text-zinc-100 truncate">{t.subject}</div>
          <div className="text-[11px] text-zinc-500">{t.shop_name} · {t.created_by_name} · {fmt(t.created_at)} · {t.screen || "sem tela"}</div>
        </div>
        <span className={`shrink-0 inline-flex items-center h-6 px-2 rounded-full text-[11px] font-bold uppercase border ${PILL[t.status]}`}>{LABEL[t.status]}</span>
      </button>
      {open && (
        <div className="pt-2 border-t border-zinc-800 space-y-2">
          <p className="text-sm text-zinc-200 whitespace-pre-wrap">{t.description}</p>
          {t.has_screenshot && img && <img src={img} alt="anexo" className="max-h-48 rounded border border-zinc-800" />}
          <div className="space-y-1.5">
            {t.messages?.map((m, i) => (
              <div key={i} className={`rounded-lg p-2 text-sm ${m.by === "admin" ? "bg-emerald-950/40 border border-emerald-800/50" : "bg-zinc-900 border border-zinc-800"}`}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{m.by === "admin" ? "Você (Suporte)" : m.by_name} · {fmt(m.created_at)}</div>
                <p className="text-zinc-100 whitespace-pre-wrap mt-0.5">{m.text}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input data-testid={`admin-ticket-reply-${t.id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Responder…" className="h-9 bg-zinc-900 border-zinc-800 flex-1 min-w-[160px]" />
            <Button data-testid={`admin-ticket-send-${t.id}`} onClick={send} disabled={sending || !reply.trim()} className="h-9 bg-green-brand hover:bg-green-brandDark text-white"><Send className="h-4 w-4" /></Button>
            <select data-testid={`admin-ticket-status-${t.id}`} value={t.status} onChange={(e) => change(e.target.value)} className="h-9 rounded-md bg-zinc-900 border border-zinc-800 text-sm px-2">
              {STATUS.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export function TicketsAdmin() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("todos");
  const focusId = new URLSearchParams(window.location.search).get("ticket");
  const [open, setOpen] = useState(!!focusId);
  const [wa, setWa] = useState("");
  const [savingWa, setSavingWa] = useState(false);
  const inputRef = useRef(null);
  const load = async () => { try { const { data } = await api.get("/admin/support/tickets"); setTickets(data); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { load(); api.get("/admin/platform-settings").then(({ data }) => setWa(data.support_whatsapp || "")).catch(() => {}); }, []);
  const change = (t) => setTickets((p) => p.map((x) => (x.id === t.id ? t : x)));
  const saveWa = async () => {
    setSavingWa(true);
    try { await api.patch("/admin/platform-settings", { support_whatsapp: wa }); toast.success("WhatsApp do suporte salvo"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSavingWa(false); }
  };
  const filtered = filter === "todos" ? tickets : tickets.filter((t) => t.status === filter);
  const open_count = tickets.filter((t) => t.status !== "resolvido").length;
  return (
    <section data-testid="tickets-admin" className="space-y-3">
      <button type="button" data-testid="tickets-admin-toggle" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 hover:bg-zinc-900">
        <span className="inline-flex items-center gap-2 font-display text-lg font-black uppercase text-zinc-100"><Ticket className="h-5 w-5 text-amber-300" /> Chamados <span className="text-xs text-zinc-400">({open_count} aberto(s))</span></span>
        <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input ref={inputRef} data-testid="support-wa-input" value={wa} onChange={(e) => setWa(e.target.value)} placeholder="WhatsApp do suporte (ex.: 11999998888)" className="h-9 bg-zinc-900 border-zinc-800 max-w-[280px]" />
            <Button data-testid="support-wa-save" onClick={saveWa} disabled={savingWa} className="h-9 bg-green-brand hover:bg-green-brandDark text-white font-bold">{savingWa && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
            <div className="flex items-center gap-1 ml-auto flex-wrap">
              {["todos", ...STATUS].map((s) => (
                <button key={s} data-testid={`tickets-filter-${s}`} onClick={() => setFilter(s)} className={`h-8 px-3 rounded-md text-[11px] font-bold uppercase border ${filter === s ? "bg-green-brand/20 border-green-700 text-green-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>{s === "todos" ? "Todos" : LABEL[s]}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {filtered.length === 0 && <div className="text-xs text-zinc-500">Nenhum chamado.</div>}
            {filtered.map((t) => <Thread key={t.id} t={t} onChanged={change} focus={t.id === focusId} />)}
          </div>
        </div>
      )}
    </section>
  );
}
